import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";

export const META_TITLE_LIMIT = 60;
export const META_DESC_LIMIT = 160;

function CharCount({ value, limit }: { value: string; limit: number }) {
  const length = value?.length ?? 0;
  return (
    <span className={`text-xs ${length > limit ? "text-destructive" : "text-muted-foreground"}`}>
      {length}/{limit}
    </span>
  );
}

// Mirror Google's own truncation so the preview shows what actually fits.
function truncate(value: string, limit: number): string {
  return value.length > limit ? `${value.slice(0, limit).trimEnd()}…` : value;
}

/**
 * The standard "Search engine listing" card — meta title/description fields
 * with a Google-style result preview below them. Shared by the page and blog
 * post editors; empty fields preview their fallbacks (title / excerpt), which
 * is exactly what ships in the built site's tags.
 */
export function SearchListingCard({
  metaTitle,
  metaDesc,
  onMetaTitleChange,
  onMetaDescChange,
  titleFallback,
  descFallback = "",
  previewUrl,
}: {
  metaTitle: string;
  metaDesc: string;
  onMetaTitleChange: (value: string) => void;
  onMetaDescChange: (value: string) => void;
  /** Used when meta title is empty — the same fallback the site renders. */
  titleFallback: string;
  /** Used when meta description is empty (e.g. a post's excerpt). */
  descFallback?: string;
  /** The page's live URL, e.g. https://site.pages.dev/blog/my-post/ */
  previewUrl: string;
}) {
  const previewTitle = truncate(metaTitle || titleFallback, META_TITLE_LIMIT);
  const previewDesc = truncate(metaDesc || descFallback, META_DESC_LIMIT);
  // Google renders the URL as "domain › path › segments".
  const breadcrumb = previewUrl
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "")
    .split("/")
    .filter(Boolean)
    .join(" › ");

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Search engine listing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="meta-title">Meta title</Label>
            <CharCount value={metaTitle} limit={META_TITLE_LIMIT} />
          </div>
          <Input
            id="meta-title"
            value={metaTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onMetaTitleChange(e.target.value)}
            placeholder={titleFallback}
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="meta-desc">Meta description</Label>
            <CharCount value={metaDesc} limit={META_DESC_LIMIT} />
          </div>
          <Input
            id="meta-desc"
            value={metaDesc}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onMetaDescChange(e.target.value)}
            placeholder="A short summary shown in search results"
          />
        </div>

        {/* Google-style result preview. Always on a white ground — that's how
            the search results page looks regardless of the portal theme. */}
        <div className="space-y-1.5 pt-1">
          <Label>Preview</Label>
          <div className="rounded-md border bg-white px-4 py-3">
            <p className="truncate text-xs text-[#4d5156]">{breadcrumb}</p>
            <p className="mt-0.5 truncate text-lg leading-snug text-[#1a0dab]">
              {previewTitle || "Page title"}
            </p>
            <p className="text-sm leading-snug text-[#4d5156]">
              {previewDesc || "Google will pick a snippet from the page content."}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            How this page can appear in a Google search result.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
